import { Html, Line, OrbitControls } from '@react-three/drei';
import { useFrame, type ThreeEvent } from '@react-three/fiber';
import { ice } from '@halvard/rules';
import { useMemo, useRef, type CSSProperties } from 'react';
import { BufferAttribute, BufferGeometry, DoubleSide, type Group, type Mesh } from 'three';
import { TILE_SIZE, squareCenter } from '../geometry.js';
import { CameraRig } from '../Scene.js';
import type { CameraView, Square } from '../types.js';
import { crackPattern, crackRibbons, crackShake } from './crack.js';
import type { IceHints, ShoveGhost, ShoveRoute, SinkReason } from './hints.js';
import {
  ICE_COLORS,
  ICE_LOOK,
  RUNESTONE_SQUARES,
  SIDE_COLORS,
  slabTop,
  TONE_COLORS,
} from './look.js';
import {
  buildTimeline,
  effectProgress,
  poseAt,
  restYaw,
  type Effect,
  WATER,
  type Keyframe,
  type Pose,
  type Timeline,
} from './timeline.js';
import { Surroundings } from './Surroundings.js';
import { WarriorModel } from './WarriorModel.js';

/** The events of the last action, played from the state before it. A new `key` replays. */
export interface IcePlayback {
  key: number;
  before: ice.IceState;
  events: readonly ice.IceEvent[];
}

export interface IceSceneProps {
  state: ice.IceState;
  hints: IceHints;
  selected: Square | null;
  cursor: Square | null;
  lastMove: readonly Square[];
  playback: IcePlayback | null;
  view: CameraView;
  facing: 'I' | 'B';
  reducedMotion: boolean;
  /** The tag over a see-through warrior that would go under. */
  sinkBadge: (reason: SinkReason) => string;
  onSquareClick: (square: Square) => void;
  onSquareHover: (square: Square | null) => void;
}

function levelOf(heights: number[][], r: number, c: number): number {
  const h = heights[r]?.[c];
  return h === undefined || h === ice.HOLE ? WATER : h;
}

/** How close a crack is to thawing, 0 to 1: it trembles harder as its round comes. */
function urgencyOf(state: ice.IceState, r: number, c: number): number | null {
  const crack = state.cracks.find((k) => k.r === r && k.c === c);
  if (!crack) return null;
  const left = crack.thawRound - state.round;
  return left <= 0 ? 1 : left === 1 ? 0.55 : 0.2;
}

/** Keeps neighbouring cracked squares from shaking in step. */
function phaseOf(r: number, c: number): number {
  return ((r * 7 + c * 13) % 10) * 0.37;
}

function worldOf(pose: Pose): [number, number, number] {
  const [x, z] = squareCenter(pose.r, pose.c);
  return [x, slabTop(pose.level) + pose.lift - pose.sink * 0.9, z];
}

export function IceScene(props: IceSceneProps) {
  const { state, hints } = props;
  const playback = props.playback;
  const timeline = useMemo<Timeline | null>(
    () => (playback ? buildTimeline(playback.before, playback.events) : null),
    [playback],
  );
  // When the current playback started, in scene seconds; `PlaybackClock` sets it on the first
  // frame after a change, before anything reads it.
  const clock = useRef<{ key: number; start: number | null }>({ key: -1, start: null });
  if (playback && clock.current.key !== playback.key)
    clock.current = { key: playback.key, start: null };

  const click = (square: Square) => (event: ThreeEvent<MouseEvent>) => {
    event.stopPropagation();
    props.onSquareClick(square);
  };
  const hover = (square: Square) => (event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation();
    props.onSquareHover(square);
  };

  // Warriors sunk by this action are drawn from the state before it until they have gone under.
  const shown = useMemo(() => {
    const now = new Map(state.warriors.map((w) => [w.id, w]));
    const gone = (playback?.before.warriors ?? []).filter((w) => !now.has(w.id));
    return [...state.warriors, ...gone];
  }, [state.warriors, playback]);
  const holders = ice.runestoneHolders(state);

  return (
    <>
      <PlaybackClock clock={clock} />
      <Surroundings seed={state.lake.seed} still={props.reducedMotion} />
      <hemisphereLight args={['#E8F2FA', '#0B1A26', 1.25]} />
      <directionalLight
        position={[4, 12, 7]}
        color="#FFF6EA"
        intensity={2.2}
        castShadow
        shadow-mapSize={[2048, 2048]}
        shadow-camera-left={-7}
        shadow-camera-right={7}
        shadow-camera-top={7}
        shadow-camera-bottom={-7}
        shadow-bias={-0.0005}
      />
      <directionalLight position={[-6, 5, -6]} color="#9CC8FF" intensity={0.5} />

      <mesh position={[0, -0.02, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={[40, 40]} />
        <meshStandardMaterial color={ICE_LOOK.water} roughness={0.7} metalness={0.05} />
      </mesh>

      {state.heights.map((row, r) =>
        row.map((h, c) =>
          h === ice.HOLE ? (
            <mesh
              key={`hole:${r}:${c}`}
              position={[squareCenter(r, c)[0], 0.001, squareCenter(r, c)[1]]}
              rotation={[-Math.PI / 2, 0, 0]}
              onClick={click({ r, c })}
              onPointerOver={hover({ r, c })}
            >
              <planeGeometry args={[TILE_SIZE, TILE_SIZE]} />
              <meshBasicMaterial color={ICE_LOOK.water} />
            </mesh>
          ) : (
            <Slab
              key={`slab:${r}:${c}`}
              square={{ r, c }}
              level={h}
              urgency={urgencyOf(state, r, c)}
              reducedMotion={props.reducedMotion}
              onClick={click({ r, c })}
              onPointerOver={hover({ r, c })}
            />
          ),
        ),
      )}

      {RUNESTONE_SQUARES.map((sq, i) => (
        <Runestone
          key={`stone:${i}`}
          square={sq}
          level={levelOf(state.heights, sq.r, sq.c)}
          holder={holders[i] ?? null}
        />
      ))}
      {ice.RUNESTONES.flatMap((zone, i) =>
        zone
          .filter((sq) => state.heights[sq.r]?.[sq.c] !== ice.HOLE)
          .map((sq) => (
            <ZoneMark
              key={`zone:${sq.r}:${sq.c}`}
              square={sq}
              level={levelOf(state.heights, sq.r, sq.c)}
              holder={holders[i] ?? null}
            />
          )),
      )}

      {state.cracks.map((crack) => (
        <Crack
          key={`crack:${crack.r}:${crack.c}`}
          seed={state.lake.seed}
          square={crack}
          level={levelOf(state.heights, crack.r, crack.c)}
          opensHole={state.heights[crack.r]?.[crack.c] === 0}
          urgency={urgencyOf(state, crack.r, crack.c) ?? 0}
          reducedMotion={props.reducedMotion}
        />
      ))}

      {props.lastMove.map((sq) => (
        <Frame
          key={`last:${sq.r}:${sq.c}`}
          square={sq}
          heights={state.heights}
          color={ICE_LOOK.lastMove}
          width={0.04}
          opacity={0.6}
        />
      ))}
      {props.selected && (
        <Frame
          square={props.selected}
          heights={state.heights}
          color={ICE_LOOK.selected}
          width={0.07}
        />
      )}
      {props.cursor && (
        <Frame
          square={props.cursor}
          heights={state.heights}
          color={ICE_LOOK.cursor}
          width={0.05}
          lift={0.03}
        />
      )}

      {hints.moves.map((sq) => {
        const [x, z] = squareCenter(sq.r, sq.c);
        return (
          <mesh
            key={`move:${sq.r}:${sq.c}`}
            position={[x, slabTop(levelOf(state.heights, sq.r, sq.c)) + 0.015, z]}
          >
            <cylinderGeometry args={[0.14, 0.14, 0.02, 28]} />
            <meshBasicMaterial color={ICE_LOOK.move} transparent opacity={0.55} />
          </mesh>
        );
      })}

      {hints.routes.map((route) => (
        <Route
          key={`route:${route.option.action.from.r}:${route.option.action.from.c}:${route.option.action.target.r}:${route.option.action.target.c}`}
          route={route}
          heights={state.heights}
        />
      ))}
      {hints.ghosts.map((ghost) => (
        <Ghost
          key={`ghost:${ghost.warrior.id}`}
          ghost={ghost}
          heights={state.heights}
          sinkBadge={props.sinkBadge}
        />
      ))}

      {shown.map((w) => (
        <AnimatedWarrior
          key={w.id}
          warrior={w}
          rest={{
            r: w.r,
            c: w.c,
            level: levelOf(state.heights, w.r, w.c),
            lift: 0,
            sink: 0,
            yaw: restYaw(w.side),
            lean: 0,
            squash: 1,
          }}
          raised={props.selected?.r === w.r && props.selected.c === w.c}
          track={timeline?.tracks.get(w.id) ?? null}
          clock={clock}
          gone={!state.warriors.some((x) => x.id === w.id)}
          reducedMotion={props.reducedMotion}
          onClick={click({ r: w.r, c: w.c })}
          onPointerOver={hover({ r: w.r, c: w.c })}
        />
      ))}

      {hints.badges.map((badge) => {
        const [x, z] = squareCenter(badge.target.r, badge.target.c);
        const y = slabTop(levelOf(state.heights, badge.target.r, badge.target.c)) + 1.05;
        const sinks = badge.tone === 'sinks-enemy' ? ' ✕' : badge.tone === 'sinks-ally' ? ' !' : '';
        return (
          <Html
            key={`badge:${badge.target.r}:${badge.target.c}`}
            position={[x, y, z]}
            center
            zIndexRange={[20, 0]}
          >
            <span style={badgeStyle(TONE_COLORS[badge.tone])}>
              {badge.strength}:{badge.wall}
              {sinks}
            </span>
          </Html>
        );
      })}

      {timeline && !props.reducedMotion && <Effects effects={timeline.effects} clock={clock} />}

      <OrbitControls makeDefault enableDamping target={[0, 0.6, 0]} />
      <CameraRig view={props.view} facing={props.facing} reducedMotion={props.reducedMotion} />
    </>
  );
}

function badgeStyle(color: string): CSSProperties {
  return {
    display: 'inline-block',
    padding: '2px 7px',
    borderRadius: 999,
    background: color,
    color: '#0C1822',
    font: '700 13px/1.2 system-ui, sans-serif',
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
  };
}

/**
 * An ice slab whose thickness eases to its level, so a thaw is seen happening. A cracked slab
 * (`urgency` not null) shivers now and then.
 */
function Slab(props: {
  square: Square;
  level: number;
  urgency: number | null;
  reducedMotion: boolean;
  onClick: (event: ThreeEvent<MouseEvent>) => void;
  onPointerOver: (event: ThreeEvent<PointerEvent>) => void;
}) {
  const ref = useRef<Mesh>(null);
  const [x, z] = squareCenter(props.square.r, props.square.c);
  const top = slabTop(props.level);
  useFrame((three, delta) => {
    const mesh = ref.current;
    if (!mesh) return;
    const t = props.reducedMotion ? 1 : 1 - Math.exp(-delta * 4);
    mesh.scale.y += (top - mesh.scale.y) * t;
    mesh.position.y = mesh.scale.y / 2;
    const shake =
      props.urgency === null || props.reducedMotion
        ? { x: 0, z: 0, twist: 0 }
        : crackShake(
            three.clock.elapsedTime,
            phaseOf(props.square.r, props.square.c),
            props.urgency,
          );
    mesh.position.x = x + shake.x;
    mesh.position.z = z + shake.z;
    mesh.rotation.y = shake.twist;
  });
  return (
    <mesh
      ref={ref}
      position={[x, top / 2, z]}
      scale={[1, top, 1]}
      castShadow
      receiveShadow
      onClick={props.onClick}
      onPointerOver={props.onPointerOver}
    >
      <boxGeometry args={[TILE_SIZE, 1, TILE_SIZE]} />
      <meshStandardMaterial
        color={ICE_COLORS[Math.min(Math.max(props.level, 0), 3)] ?? ICE_COLORS[0]}
        roughness={0.6}
        metalness={0.05}
        transparent={props.level === 0}
        opacity={props.level === 0 ? 0.88 : 1}
      />
    </mesh>
  );
}

/**
 * A rune ring carved into the ice of the floe's centre square, glowing in the colour of the side
 * that holds it. It lies flat so a warrior can stand on it.
 */
function Runestone(props: { square: Square; level: number; holder: ice.Side | null }) {
  const [x, z] = squareCenter(props.square.r, props.square.c);
  const glow = props.holder ? SIDE_COLORS[props.holder].body : ICE_LOOK.stoneDark;
  const y = slabTop(props.level) + 0.006;
  return (
    <group>
      <group position={[x, y, z]} rotation={[-Math.PI / 2, 0, 0]}>
        <mesh>
          <ringGeometry args={[0.3, 0.4, 40]} />
          <meshBasicMaterial color={glow} transparent opacity={props.holder ? 0.95 : 0.6} />
        </mesh>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <mesh
            key={i}
            rotation={[0, 0, (i * Math.PI) / 3]}
            position={[Math.cos((i * Math.PI) / 3) * 0.2, Math.sin((i * Math.PI) / 3) * 0.2, 0]}
          >
            <planeGeometry args={[0.03, 0.12]} />
            <meshBasicMaterial color={glow} transparent opacity={props.holder ? 0.95 : 0.6} />
          </mesh>
        ))}
      </group>
    </group>
  );
}

/**
 * A thin line carved just inside the edge of each square of a runestone's 3×3 floe, glowing like
 * its stone, so the whole zone reads at a glance and not only the centre. It sits on the square's
 * own ice, whatever its height, and clear of the selection and last-move frames at the edge.
 */
function ZoneMark(props: { square: Square; level: number; holder: ice.Side | null }) {
  const [x, z] = squareCenter(props.square.r, props.square.c);
  const glow = props.holder ? SIDE_COLORS[props.holder].body : ICE_LOOK.stoneDark;
  const width = 0.028;
  const half = TILE_SIZE / 2 - 0.1;
  const edges: [number, number, number, number][] = [
    [0, -half, half * 2 + width, width],
    [0, half, half * 2 + width, width],
    [-half, 0, width, half * 2 + width],
    [half, 0, width, half * 2 + width],
  ];
  return (
    <group position={[x, slabTop(props.level) + 0.006, z]}>
      {edges.map(([dx, dz, w, d]) => (
        <mesh key={`${dx}:${dz}`} position={[dx, 0, dz]}>
          <boxGeometry args={[w, 0.008, d]} />
          <meshBasicMaterial color={glow} transparent opacity={props.holder ? 0.85 : 0.5} />
        </mesh>
      ))}
    </group>
  );
}

/**
 * Jagged fracture lines branching across the slab, red when the thaw will open a hole. They
 * spread out from the point of impact when the crack appears, and shiver with the slab.
 */
function Crack(props: {
  seed: number;
  square: Square;
  level: number;
  opensHole: boolean;
  urgency: number;
  reducedMotion: boolean;
}) {
  const ref = useRef<Group>(null);
  const born = useRef<number | null>(null);
  const { r, c } = props.square;
  const [x, z] = squareCenter(r, c);
  const y = slabTop(props.level) + 0.008;
  const color = props.opensHole ? ICE_LOOK.crackHole : ICE_LOOK.crack;
  const [line, groove] = useMemo(() => {
    const pattern = crackPattern(props.seed, r, c);
    const ribbon = (widen: number) => {
      const { positions, indices } = crackRibbons(pattern, widen);
      const g = new BufferGeometry();
      g.setAttribute('position', new BufferAttribute(positions, 3));
      g.setIndex(indices);
      return g;
    };
    return [ribbon(1), ribbon(2.2)];
  }, [props.seed, r, c]);
  useFrame((three) => {
    const group = ref.current;
    if (!group) return;
    const now = three.clock.elapsedTime;
    born.current ??= now;
    const grow = props.reducedMotion ? 1 : Math.min((now - born.current) / 0.35, 1);
    const shake = props.reducedMotion
      ? { x: 0, z: 0, twist: 0 }
      : crackShake(now, phaseOf(r, c), props.urgency);
    group.scale.set(grow, 1, grow);
    group.position.set(x + shake.x, y, z + shake.z);
    group.rotation.y = shake.twist;
  });
  return (
    <group ref={ref} position={[x, y, z]}>
      <mesh geometry={groove} position={[0, -0.002, 0]}>
        <meshBasicMaterial
          color={ICE_LOOK.crackShadow}
          transparent
          opacity={0.35}
          side={DoubleSide}
        />
      </mesh>
      <mesh geometry={line} position={[0, 0.002, 0]}>
        <meshBasicMaterial color={color} side={DoubleSide} />
      </mesh>
    </group>
  );
}

function Frame(props: {
  square: Square;
  heights: number[][];
  color: string;
  width: number;
  lift?: number;
  opacity?: number;
}) {
  const { r, c } = props.square;
  const [x, z] = squareCenter(r, c);
  const y = slabTop(levelOf(props.heights, r, c));
  const half = TILE_SIZE / 2 - props.width / 2;
  const edges: [number, number, number, number][] = [
    [0, -half, TILE_SIZE, props.width],
    [0, half, TILE_SIZE, props.width],
    [-half, 0, props.width, TILE_SIZE],
    [half, 0, props.width, TILE_SIZE],
  ];
  return (
    <group position={[x, y + 0.012 + (props.lift ?? 0), z]}>
      {edges.map(([dx, dz, w, d]) => (
        <mesh key={`${dx}:${dz}`} position={[dx, 0, dz]}>
          <boxGeometry args={[w, 0.02, d]} />
          <meshBasicMaterial
            color={props.color}
            transparent={props.opacity !== undefined}
            opacity={props.opacity ?? 1}
          />
        </mesh>
      ))}
    </group>
  );
}

/** The walk to the approach square, the step into the enemy, and an arrow for the push. */
function Route({ route, heights }: { route: ShoveRoute; heights: number[][] }) {
  const { option } = route;
  const color = TONE_COLORS[route.tone];
  const strong = route.emphasis === 'strong';
  const points = option.path.map((sq) => {
    const [x, z] = squareCenter(sq.r, sq.c);
    return [x, slabTop(levelOf(heights, sq.r, sq.c)) + 0.1, z] as [number, number, number];
  });
  const end = points.at(-1) ?? [0, 0, 0];
  const { dr, dc } = option.direction;
  const angle = Math.atan2(dc, dr);
  const [fx, fz] = squareCenter(option.action.from.r, option.action.from.c);
  const fromY = slabTop(levelOf(heights, option.action.from.r, option.action.from.c));
  return (
    <group>
      <Line
        points={points}
        color={color}
        lineWidth={strong ? 5 : 2}
        transparent
        opacity={strong ? 1 : 0.4}
      />
      {/* The approach square: a flat diamond. */}
      <mesh position={[fx, fromY + 0.03, fz]} rotation={[-Math.PI / 2, 0, Math.PI / 4]}>
        <planeGeometry args={[0.22, 0.22]} />
        <meshBasicMaterial color={color} transparent opacity={strong ? 1 : 0.4} />
      </mesh>
      {/* Arrow past the enemy, in the direction the line will slide. */}
      <group
        position={[end[0] + dc * 0.55, end[1] + 0.05, end[2] + dr * 0.55]}
        rotation={[0, angle, 0]}
      >
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <coneGeometry args={[strong ? 0.13 : 0.09, 0.28, 16]} />
          <meshBasicMaterial color={color} transparent opacity={strong ? 1 : 0.4} />
        </mesh>
      </group>
    </group>
  );
}

/** A see-through warrior where the line would land, marked if it goes under. */
function Ghost({
  ghost,
  heights,
  sinkBadge,
}: {
  ghost: ShoveGhost;
  heights: number[][];
  sinkBadge: (reason: SinkReason) => string;
}) {
  const [x, z] = squareCenter(ghost.to.r, ghost.to.c);
  const level = levelOf(heights, ghost.to.r, ghost.to.c);
  const y = slabTop(ghost.sunk === 'off' || ghost.sunk === 'hole' ? WATER : level);
  return (
    <group position={[x, y, z]}>
      <WarriorModel side={ghost.warrior.side} type={ghost.warrior.type} opacity={0.35} />
      {ghost.sunk && (
        <Html position={[0, 0.95, 0]} center zIndexRange={[20, 0]}>
          <span style={badgeStyle(ICE_LOOK.crackHole)}>✕ {sinkBadge(ghost.sunk)}</span>
        </Html>
      )}
    </group>
  );
}

interface PlaybackClockRef {
  current: { key: number; start: number | null };
}

/** Starts the playback clock on the first frame of a new playback. Rendered first. */
function PlaybackClock({ clock }: { clock: PlaybackClockRef }) {
  useFrame((three) => {
    if (clock.current.start === null) clock.current.start = three.clock.elapsedTime;
  });
  return null;
}

function elapsedOf(clock: PlaybackClockRef, now: number): number | null {
  const start = clock.current.start;
  return start === null ? null : now - start;
}

/** Snow rings and specks on landing; a splash ring and droplets when a warrior goes under. */
function Effects({ effects, clock }: { effects: readonly Effect[]; clock: PlaybackClockRef }) {
  return (
    <>
      {effects.map((effect, i) => (
        <Burst key={`${effect.kind}:${i}`} effect={effect} clock={clock} />
      ))}
    </>
  );
}

const SPECKS = [0, 1, 2, 3, 4, 5].map((i) => (i * Math.PI) / 3 + 0.4);

function Burst({ effect, clock }: { effect: Effect; clock: PlaybackClockRef }) {
  const group = useRef<Group>(null);
  const ring = useRef<Mesh>(null);
  const specks = useRef<(Mesh | null)[]>([]);
  const [x, z] = squareCenter(effect.r, effect.c);
  const splash = effect.kind === 'splash';
  const color = splash ? '#BFE3F2' : '#FFFFFF';
  useFrame((three) => {
    const g = group.current;
    if (!g) return;
    const elapsed = elapsedOf(clock, three.clock.elapsedTime);
    const k = elapsed === null ? null : effectProgress(effect, elapsed);
    g.visible = k !== null;
    if (k === null) return;
    const out = 1 - (1 - k) * (1 - k);
    const r = ring.current;
    if (r) {
      const radius = (0.12 + out * (splash ? 0.55 : 0.38)) * effect.size;
      r.scale.set(radius, radius, 1);
      (r.material as { opacity: number }).opacity = (splash ? 0.8 : 0.6) * (1 - k);
    }
    specks.current.forEach((speck, i) => {
      if (!speck) return;
      const angle = SPECKS[i] ?? 0;
      const reach = out * (splash ? 0.35 : 0.3) * effect.size;
      const height = (splash ? 0.55 : 0.18) * effect.size * 4 * k * (1 - k);
      speck.position.set(Math.cos(angle) * reach, height, Math.sin(angle) * reach);
      (speck.material as { opacity: number }).opacity = 0.9 * (1 - k);
    });
  });
  return (
    <group ref={group} position={[x, slabTop(effect.level) + 0.02, z]} visible={false}>
      <mesh ref={ring} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[0.82, 1, 32]} />
        <meshBasicMaterial color={color} transparent opacity={0} depthWrite={false} />
      </mesh>
      {SPECKS.map((_, i) => (
        <mesh
          key={i}
          ref={(m) => {
            specks.current[i] = m;
          }}
        >
          <sphereGeometry args={[splash ? 0.035 : 0.028, 6, 4]} />
          <meshBasicMaterial color={color} transparent opacity={0} depthWrite={false} />
        </mesh>
      ))}
    </group>
  );
}

function AnimatedWarrior(props: {
  warrior: ice.Warrior;
  rest: Pose;
  raised: boolean;
  track: Keyframe[] | null;
  clock: PlaybackClockRef;
  gone: boolean;
  reducedMotion: boolean;
  onClick: (event: ThreeEvent<MouseEvent>) => void;
  onPointerOver: (event: ThreeEvent<PointerEvent>) => void;
}) {
  const ref = useRef<Group>(null);
  const turn = useRef<Group>(null);
  const tilt = useRef<Group>(null);
  const body = useRef<Group>(null);
  const [x, y, z] = worldOf(props.rest);
  const rest = restYaw(props.warrior.side);
  useFrame((three, delta) => {
    const group = ref.current;
    const yaw = turn.current;
    const lean = tilt.current;
    const squash = body.current;
    if (!group || !yaw || !lean || !squash) return;
    const elapsed = elapsedOf(props.clock, three.clock.elapsedTime) ?? 0;
    const track = props.reducedMotion ? null : props.track;
    const last = track?.at(-1);
    if (track && last && elapsed <= last.t + 0.05) {
      const pose = poseAt(track, elapsed);
      if (pose) {
        const [px, py, pz] = worldOf(pose);
        group.position.set(px, py, pz);
        group.visible = pose.sink < 0.98;
        yaw.rotation.y = pose.yaw;
        lean.rotation.x = pose.lean;
        // Squash keeps the volume: shorter means a little wider.
        const wide = 1 / Math.sqrt(pose.squash);
        squash.scale.set(wide, pose.squash, wide);
        return;
      }
    }
    group.visible = !props.gone;
    const lift = props.raised ? 0.12 : 0;
    const t = props.reducedMotion ? 1 : 1 - Math.exp(-delta * 12);
    group.position.x += (x - group.position.x) * t;
    group.position.z += (z - group.position.z) * t;
    group.position.y += (y + lift - group.position.y) * t;
    yaw.rotation.y += (rest - yaw.rotation.y) * t;
    lean.rotation.x += (0 - lean.rotation.x) * t;
    squash.scale.set(1, 1, 1);
  });
  return (
    <group
      ref={ref}
      position={[x, y, z]}
      onClick={props.onClick}
      onPointerOver={props.onPointerOver}
    >
      <group ref={turn} rotation={[0, rest, 0]}>
        <group ref={tilt}>
          <group ref={body}>
            <WarriorModel side={props.warrior.side} type={props.warrior.type} facing={0} />
          </group>
        </group>
      </group>
    </group>
  );
}
